import { useRef, useState } from "react";
import { Image as ImageIcon, Video, Loader2, X, Plus, Link as LinkIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { uploadMedia, resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";

const detectYoutubeVimeo = (url) => {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: "youtube", id: yt[1], embed: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "vimeo", id: vm[1], embed: `https://player.vimeo.com/video/${vm[1]}` };
  return null;
};

const DropZone = ({ kind, accept, sizeHint, onUploaded, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);
  const isVideo = kind === "video";
  const Icon = isVideo ? Video : ImageIcon;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const result = await uploadMedia(file, kind, setProgress);
      onUploaded(result);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault(); setDrag(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
      }}
      onClick={() => inputRef.current?.click()}
      data-testid={testId}
      className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${drag ? "border-saffron-400 bg-saffron-50/40" : "border-slate-300 hover:border-saffron-300"}`}
    >
      {uploading ? (
        <div className="flex flex-col items-center gap-2 text-navy-600">
          <Loader2 className="h-6 w-6 animate-spin text-saffron-600" />
          <div className="text-sm font-medium">Uploading... {progress}%</div>
          <div className="w-40 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-saffron-600 transition-all" style={{ width: `${progress}%` }} /></div>
        </div>
      ) : (
        <>
          <Icon className="h-8 w-8 text-slate-400 mx-auto" />
          <p className="mt-3 text-sm font-medium text-navy-700">Drop your {isVideo ? "video" : "image"} or click to browse</p>
          <p className="text-xs text-navy-500 mt-1">{sizeHint}</p>
        </>
      )}
      <input
        ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};

export const CoverUploader = ({ value, onChange }) => (
  <div>
    {value ? (
      <div className="relative rounded-2xl overflow-hidden border border-warm-200 aspect-[16/9] bg-slate-100">
        <img src={resolveMediaUrl(value)} alt="Cover" className="h-full w-full object-cover" />
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid="cover-remove-btn"
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80 transition-colors"
          aria-label="Remove cover"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    ) : (
      <DropZone
        kind="cover"
        accept="image/png,image/jpeg,image/webp"
        sizeHint="1600×900 recommended · PNG, JPG, WEBP · max 8MB"
        onUploaded={(r) => onChange(r.url)}
        testId="cover-dropzone"
      />
    )}
  </div>
);

export const GalleryUploader = ({ value = [], onChange, max = 6 }) => {
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const addImage = (r) => onChange([...value, r.url]);
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {value.map((src, i) => (
          <div key={i} className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100 border border-warm-200 group">
            <img src={resolveMediaUrl(src)} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`gallery-remove-${i}`}
              className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {value.length < max && (
          <label className="rounded-2xl border-2 border-dashed border-slate-300 aspect-[4/3] grid place-items-center cursor-pointer hover:border-saffron-300 transition-colors" data-testid="gallery-add-tile">
            <div className="flex flex-col items-center gap-1 text-navy-500">
              <Plus className="h-5 w-5" />
              <span className="text-xs font-medium">Add image</span>
            </div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const r = await uploadMedia(file, "gallery");
                  addImage(r);
                  toast.success("Added to gallery");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Upload failed");
                }
                e.target.value = "";
              }}
            />
          </label>
        )}
      </div>
      <p className="mt-2 text-xs text-navy-500">Up to {max} images · PNG, JPG, WEBP · max 8MB each</p>
    </div>
  );
};

export const VideoUploader = ({ value, onChange }) => {
  const [mode, setMode] = useState(value && !value.startsWith("/api/") ? "url" : "upload");
  const embed = value ? detectYoutubeVimeo(value) : null;
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setMode("upload")}
          data-testid="video-mode-upload"
          className={`px-3 py-1.5 text-sm font-medium rounded-xl border transition-colors ${mode === "upload" ? "border-saffron-500 bg-saffron-50 text-saffron-700" : "border-warm-200 text-navy-700 hover:border-saffron-200"}`}
        >Upload</button>
        <button
          type="button"
          onClick={() => setMode("url")}
          data-testid="video-mode-url"
          className={`px-3 py-1.5 text-sm font-medium rounded-xl border transition-colors ${mode === "url" ? "border-saffron-500 bg-saffron-50 text-saffron-700" : "border-warm-200 text-navy-700 hover:border-saffron-200"}`}
        >YouTube / Vimeo URL</button>
      </div>
      {value ? (
        <div className="relative rounded-2xl overflow-hidden border border-warm-200 aspect-video bg-black">
          {embed ? (
            <iframe title="Preview" src={embed.embed} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          ) : (
            <video controls src={resolveMediaUrl(value)} className="w-full h-full" />
          )}
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid="video-remove-btn"
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/70 text-white grid place-items-center hover:bg-black/90 transition-colors"
            aria-label="Remove video"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : mode === "upload" ? (
        <DropZone
          kind="video"
          accept="video/mp4,video/quicktime,video/webm"
          sizeHint="60–90 sec pitch · MP4/MOV/WEBM · max 100MB"
          onUploaded={(r) => onChange(r.url)}
          testId="video-dropzone"
        />
      ) : (
        <div className="rounded-2xl border border-warm-200 p-4">
          <div className="flex items-center gap-2 text-sm text-navy-600 mb-2">
            <LinkIcon className="h-4 w-4" /> Paste a YouTube or Vimeo link
          </div>
          <Input
            type="url"
            placeholder="https://www.youtube.com/watch?v=..."
            onChange={(e) => {
              const url = e.target.value.trim();
              if (!url) { onChange(""); return; }
              const parsed = detectYoutubeVimeo(url);
              if (parsed || url.startsWith("http")) onChange(url);
            }}
            data-testid="video-url-input"
            className="h-11 rounded-xl border-warm-200"
          />
          <p className="mt-2 text-xs text-navy-500">Auto-embeds YouTube & Vimeo links.</p>
        </div>
      )}
    </div>
  );
};
